import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import * as moment from "moment";
import {
  AppLoadError,
  EEliOption,
  EnglishProficiency,
  IAppError,
  IEnglishProficiency,
} from "../shared/models";
import { EnglishProficiencyService } from "../shared/services/api/english-proficiency.service";
import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import { HttpErrorResponse } from "@angular/common/http";
import { SharedStoreService } from "./shared-store.service";
import { CountryListService } from "../shared/services/api/country-list.service";
import { CountryListStoreService } from "./country-list-store.service";
import { getSafe } from "../utils";
import { formatDate } from "@angular/common";
import { IEnglishProficiencyAPI } from "../shared/models/englishProficiency.models";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class EnglishProficiencyStoreService {
  private readonly _english_proficiency = new BehaviorSubject<
    IEnglishProficiency | IEnglishProficiencyAPI | null
  >(null);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<AppLoadError | Error | null>(
    null
  );
  private readonly _saving = new BehaviorSubject<boolean>(false);

  englishProficiency$: Observable<
    IEnglishProficiency | IEnglishProficiencyAPI | null
  > = this._english_proficiency.asObservable();
  loading$: Observable<boolean> = this._loading.asObservable();
  error$: Observable<AppLoadError | Error | null> = this._error.asObservable();
  saving$: Observable<boolean> = this._saving.asObservable();

  // get current value

  get englishProf() {
    return this._english_proficiency.getValue();
  }

  constructor(
    private service: EnglishProficiencyService,
    private applicatInfoStore: ApplicantInfoStoreService,
    private CountryListStore: CountryListStoreService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  selectEnglishProficiency(): Observable<
    IEnglishProficiency | IEnglishProficiencyAPI | null
  > {
    return this.englishProficiency$.pipe(map((englishProf) => englishProf));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | Error | null> {
    return this.error$.pipe(map((error) => error));
  }

  selectSaving(): Observable<boolean> {
    // Provide a method to check saving state
    return this.saving$.pipe(map((saving) => saving));
  }

  selectEnglishProfReview() {
    return combineLatest([
      this.englishProficiency$ as Observable<EnglishProficiency>,
      this.CountryListStore.countryList$,
    ]).pipe(
      map(([englishProf, countryList]) => {
        if (englishProf && countryList.length > 0) {
          const formatDate = (date) => moment.utc(date).format("LL");
          switch (englishProf.eliOption) {
            case EEliOption.SUBMIT_SCORE:
              return {
                eliOption: englishProf.eliOption,
                ...englishProf.test,
                country: getSafe(
                  () =>
                    countryList.find(
                      (country) => country.code === englishProf.test.country
                    ).name,
                  englishProf.test.country
                ),
                date: formatDate(englishProf.test.date),
              };
            case EEliOption.ATTEND_PARTNERSHIP_INSTITUTIONS:
              return {
                eliOption: englishProf.eliOption,
                name: englishProf.partner.name,
                dateCompletion: formatDate(englishProf.partner.dateCompletion),
              };
            case EEliOption.ATTEND_ELI:
              return { eliOption: "Attend ELI at Newnham" };
            case EEliOption.ATTEND_SLC:
              return { eliOption: "Attend ELI Online" };
            case EEliOption.NOT_APPLICABLE:
              return {
                eliOption: "Not Applicable/My primary language is English",
              };
            case EEliOption.ATTEND_DPP:
              return {
                eliOption: "Attend Degree Prep Program at Newnham",
              };
            default:
              return englishProf.eliOption;
          }
        }
        return null;
      })
    );
  }

  /**
   * @GET
   *
   */
  loadEnglishProficiency(): Observable<EnglishProficiency> {
    this._loading.next(true);

    return combineLatest([
      this.service.getEnglishProficiency(),
      this.applicatInfoStore.selectApplicationID(),
    ]).pipe(
      map(([englishProf, appId]) => {
        if (!englishProf || Object.entries(englishProf).length === 0) {
          return new EnglishProficiency(appId);
        } else {
          return new EnglishProficiency(
            appId,
            englishProf.EliOption as EEliOption,
            {
              name: englishProf.TestName,
              score: englishProf.TestScore,
              date: englishProf.TestDate,
              country: englishProf.TestCountry,
            },
            {
              name: englishProf.PartnerName,
              dateCompletion: englishProf.PartnerDateCompletion,
            },
            englishProf.uploadDocument
          );
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppLoadError(error.id, error.message)
          : new AppLoadError();

        return of(null);
      }),
      tap((englishProf) => {
        this._english_proficiency.next(englishProf);
        this._loading.next(false);
      })
    );
  }

  /**
   * @POST
   */
  submitEnglishProficiency(payload: IEnglishProficiency) {
    this._saving.next(true);

    // format payload as in the backend

    const englishProf: IEnglishProficiencyAPI = {
      EliOption: payload.eliOption,
      TestName: payload?.test?.name,
      TestScore: payload?.test?.score,
      TestDate: payload?.test?.date,
      TestCountry: payload?.test?.country,
      PartnerName: payload?.partner?.name,
      PartnerDateCompletion: payload?.partner?.dateCompletion,
      uploadDocument: payload?.uploadDocument,
    };

    return this.service.postEnglishProficiency(englishProf).pipe(
      map((data) => {
        this._english_proficiency.next(payload);

        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "3",
          step_name: "english-proficiency",
        });

        this.sharedStore.triggerNavigationAfterSubmit("ENGLISH_PROFICIENCY");
        return payload;
      }), // Success, payload returned
      catchError((err: any) => {
        const error = err instanceof Error ? err : new AppLoadError();
        this._error.next(error);
        return of(null);
      }),
      tap(() => this._saving.next(false))
    );
  }

  clearError(): void {
    this._error.next(null);
  }
}
